import React, { useState, useEffect, useCallback } from 'react';
import { Settings, Table2, Save, RefreshCw, ChevronDown, ChevronUp, LayoutTemplate, Plus, Trash2, X, Sparkles, Scan, Download, Wrench, CheckSquare, Search, Clipboard, ArrowLeftRight, Ruler, Trash } from 'lucide-react';
import FindReplaceModal from './FindReplaceModal';
// 🔧 LearningPanel, FileText, BrainCircuit 삭제 (탭 제거됨)

/**
 * RightPanelTabs - 오른쪽 패널 탭 컴포넌트
 * 
 * 3개 탭으로 구성:
 * 1. 필드 설정 - 템플릿 필드 편집 (description, exampleValue)
 * 2. 프롬프트 - 자동 생성된 프롬프트 보기/편집
 * 3. 추출 결과 - PageBasedGridPanel 또는 기존 결과 UI
 */
const RightPanelTabs = ({
    // 공통 props
    selectedFile,
    templates = {},
    selectedTemplateId = '',
    apiBaseUrl,

    // 프롬프트 관련
    initialPrompt = '',
    availableModels = [],
    onRun,
    isProcessing = false,

    // 결과 관련 - children으로 전달받음
    children,

    // 템플릿 업데이트 콜백
    onTemplateUpdate = null,
    // 템플릿 관리 열기 콜백
    onOpenTemplateManager = null,
    // ⭐ 추출 결과 초기화 콜백
    onReset = null,
    // 🎯 초기 활성 탭 (기본: 'results' - 추출 결과)
    initialActiveTab = 'results',
    // 📄 페이지 리스트 관련 props
    totalPages = 0,
    checkedPages = [],
    selectedPageNumber = 1,
    onPageChange = null,
    onPageToggle = null,
    // 🔧 문서추출 툴바 props
    onExtract = null,
    isExtracting = false,
    extractLabel = '딥러닝',
    extractDisabled = false,
    onCancelExtract = null,
    onExcelDownload = null,
    // 템플릿 변경 콜백
    onTemplateChange = null,
}) => {
    // 📄 디버그: 페이지 리스트 props 확인
    console.log('📄 [RightPanelTabs] 페이지 리스트 props:', { totalPages, checkedPages, selectedPageNumber });

    // 탭 상태
    const [activeTab, setActiveTab] = useState(initialActiveTab); // 'fields' | 'prompt' | 'results'

    // 필드 편집 상태
    const [editableFields, setEditableFields] = useState([]);
    const [editableTableFields, setEditableTableFields] = useState([]);
    const [hasChanges, setHasChanges] = useState(false);
    const [verifyEnabled, setVerifyEnabled] = useState(false); // 검증 체크박스 (UI만, 추후 구현)
    const [showToolsMenu, setShowToolsMenu] = useState(false); // 도구 드롭다운
    const [showFindReplace, setShowFindReplace] = useState(false); // 찾기/바꾸기 모달
    const toolsMenuRef = React.useRef(null);
    const [isSaving, setIsSaving] = useState(false);
    const [currentTemplate, setCurrentTemplate] = useState(null);

    // 도구 메뉴 외부 클릭 닫기
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (toolsMenuRef.current && !toolsMenuRef.current.contains(e.target)) {
                setShowToolsMenu(false);
            }
        };
        if (showToolsMenu) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showToolsMenu]);

    // 필드 추가 모달 상태
    const [showAddFieldModal, setShowAddFieldModal] = useState(false);
    const [newField, setNewField] = useState({ key: '', type: 'string', description: '', exampleValue: '' });

    // 🔧 Shift+클릭 범위 선택을 위한 마지막 클릭 페이지 추적
    const [lastClickedPage, setLastClickedPage] = useState(null);

    // 🔧 확장 편집 모달 상태 (긴 텍스트 편집용)
    const [expandedEditModal, setExpandedEditModal] = useState({
        isOpen: false,
        fieldType: '', // 'single' | 'table'
        fieldIndex: -1,
        tableIndex: -1, // 테이블 컬럼인 경우
        colIndex: -1,   // 테이블 컬럼인 경우
        fieldName: '',
        fieldKey: '',   // 'description' | 'exampleValue'
        value: ''
    });

    // 확장 편집 모달 열기
    const openExpandedEdit = useCallback((fieldType, fieldIndex, fieldName, fieldKey, value, tableIndex = -1, colIndex = -1) => {
        setExpandedEditModal({
            isOpen: true,
            fieldType,
            fieldIndex,
            tableIndex,
            colIndex,
            fieldName,
            fieldKey,
            value
        });
    }, []);

    // 확장 편집 모달 저장 (핸들러 함수들보다 먼저 정의되므로 의존성에 함수명만 추가)
    const saveExpandedEdit = useCallback(() => {
        const { fieldType, fieldIndex, tableIndex, colIndex, fieldKey, value } = expandedEditModal;

        // 단일 필드 업데이트
        if (fieldType === 'single') {
            setEditableFields(prev => {
                const updated = [...prev];
                updated[fieldIndex] = { ...updated[fieldIndex], [fieldKey]: value };
                return updated;
            });
            setHasChanges(true);
        }
        // 테이블 컬럼 업데이트
        else if (fieldType === 'table') {
            setEditableTableFields(prev => {
                const updated = JSON.parse(JSON.stringify(prev));
                if (updated[tableIndex]?.columns?.[colIndex]) {
                    updated[tableIndex].columns[colIndex][fieldKey] = value;
                }
                return updated;
            });
            setHasChanges(true);
        }

        setExpandedEditModal(prev => ({ ...prev, isOpen: false }));
    }, [expandedEditModal]);

    // 🆕 확장 편집 모달용 AI 가이드 상태
    const [isEnhancingModalPrompt, setIsEnhancingModalPrompt] = useState(false);

    // 🆕 확장 편집 모달용 AI 가이드 핸들러
    const handleEnhanceModalPrompt = useCallback(async () => {
        const { value, fieldName, fieldKey } = expandedEditModal;
        if (!value.trim()) {
            return;
        }

        try {
            setIsEnhancingModalPrompt(true);

            const res = await fetch(`${apiBaseUrl}/api/templates/enhance-system-prompt`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userInput: value,
                    templateName: currentTemplate?.name || '',
                    fields: [fieldName],
                    tableFields: [],
                    context: fieldKey === 'description' ? '필드 설명' : '예시 값'
                })
            });

            const data = await res.json();
            if (data.success && data.enhancedPrompt) {
                setExpandedEditModal(prev => ({ ...prev, value: data.enhancedPrompt }));
            }
        } catch (error) {
            console.error('AI 가이드 실패:', error);
        } finally {
            setIsEnhancingModalPrompt(false);
        }
    }, [expandedEditModal, apiBaseUrl, currentTemplate]);


    // 프롬프트 상태
    const [prompt, setPrompt] = useState(initialPrompt);
    const [selectedModel, setSelectedModel] = useState(availableModels[0] || 'gemini-2.5-flash');

    // 필드 파싱 함수 (구형/평탄화된 fields 지원)
    const parseFields = useCallback((template) => {
        let single = [];
        let table = [];

        // 1. 명시적으로 분리된 경우
        if (template.singleFields || template.tableFields) {
            single = template.singleFields ? JSON.parse(JSON.stringify(template.singleFields)) : [];

            // tableFields의 columns 내 'example' 속성을 'exampleValue'로 정규화
            if (template.tableFields) {
                table = JSON.parse(JSON.stringify(template.tableFields)).map(t => ({
                    ...t,
                    columns: (t.columns || []).map(col => ({
                        ...col,
                        // 'example' 속성이 있으면 'exampleValue'로 매핑
                        exampleValue: col.exampleValue || col.example || ''
                    }))
                }));
            }
        }
        // 2. 통합된 fields 배열인 경우
        else if (template.fields && Array.isArray(template.fields)) {
            single = template.fields.map(f => {
                if (typeof f === 'string') {
                    return {
                        key: f,
                        type: 'string',
                        description: '',
                        exampleValue: template.examples?.[f] || ''
                    };
                }
                return {
                    ...f,
                    // 객체 필드인 경우 exampleValue가 없으면 examples 맵에서 찾음
                    exampleValue: f.exampleValue || f.example || template.examples?.[f.key] || ''
                };
            });
            // 테이블 필드는 없다고 가정 (메타데이터 부족)
            table = [];
        }

        return { single, table };
    }, []);

    // 프롬프트 생성 함수
    const generatePromptFromFields = useCallback((templateName, singleFields, tableFields) => {
        if (!singleFields?.length && !tableFields?.length) {
            return `이 문서에서 정보를 추출해주세요. 템플릿: ${templateName || '미지정'}`;
        }

        const schemaFields = {};
        singleFields?.forEach(field => {
            schemaFields[field.key] = {
                type: field.type || 'string',
                description: field.description || field.displayName || field.key,
                ...(field.exampleValue && { example: field.exampleValue })
            };
        });

        if (tableFields?.length > 0) {
            tableFields.forEach(table => {
                const tableName = table.tableName || 'items';
                schemaFields[tableName] = {
                    type: 'array',
                    items: {}
                };
                table.columns?.forEach(col => {
                    schemaFields[tableName].items[col.key] = {
                        type: col.type || 'string',
                        description: col.description || col.displayName || col.key,
                        ...(col.exampleValue && { example: col.exampleValue })
                    };
                });
            });
        }

        return `다음 JSON 스키마에 맞춰 문서에서 정보를 추출해주세요.

템플릿: ${templateName}

스키마:
${JSON.stringify(schemaFields, null, 2)}

반드시 위 스키마의 모든 필드를 포함하여 JSON 형식으로 응답해주세요.`;
    }, []);

    // 🔧 템플릿 버전 키 (내용 변경 감지용)
    const templateVersionKey = React.useMemo(() => {
        if (selectedTemplateId && templates) {
            const template = Object.values(templates).find(t => t.id === selectedTemplateId);
            if (template) {
                // singleFields의 exampleValue들을 조합해서 버전 키 생성
                const singleKeys = (template.singleFields || template.fields || [])
                    .map(f => typeof f === 'string' ? f : `${f.key}:${f.exampleValue || ''}`).join('|');
                const tableKeys = (template.tableFields || [])
                    .map(t => (t.columns || []).map(c => `${c.key}:${c.exampleValue || ''}`).join(',')).join('|');
                return `${template.id}-${singleKeys}-${tableKeys}`;
            }
        }
        return '';
    }, [selectedTemplateId, templates]);

    // 템플릿 선택 시 필드 로드 (버전 키가 변경되면 다시 로드)
    useEffect(() => {
        if (selectedTemplateId && templates) {
            const template = Object.values(templates).find(t => t.id === selectedTemplateId);
            if (template) {
                setCurrentTemplate(template);

                // 필드 파싱 및 로드
                const { single, table } = parseFields(template);
                setEditableFields(single);
                setEditableTableFields(table);
                setHasChanges(false);

                // 프롬프트 생성
                if (!template.customPrompt) {
                    const generatedPrompt = generatePromptFromFields(
                        template.name,
                        single,
                        table
                    );
                    setPrompt(generatedPrompt);
                } else {
                    setPrompt(template.customPrompt);
                }

                console.log('🔄 RightPanelTabs: 템플릿 데이터 갱신됨', template.name);
            }
        }
    }, [selectedTemplateId, templateVersionKey, generatePromptFromFields, parseFields]);

    // 필드 변경 핸들러
    const handleFieldChange = useCallback((index, field, value) => {
        setEditableFields(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: value };
            return updated;
        });
        setHasChanges(true);
    }, []);

    // 필드 추가 핸들러
    const handleAddField = useCallback(() => {
        if (!newField.key.trim()) return;
        setEditableFields(prev => [...prev, { ...newField }]);
        setNewField({ key: '', type: 'string', description: '', exampleValue: '' });
        setShowAddFieldModal(false);
        setHasChanges(true);
    }, [newField]);

    // 필드 삭제 핸들러
    const handleRemoveField = useCallback((index) => {
        setEditableFields(prev => prev.filter((_, i) => i !== index));
        setHasChanges(true);
    }, []);

    // 테이블 컬럼 변경 핸들러  
    const handleTableColumnChange = useCallback((tableIndex, colIndex, field, value) => {
        setEditableTableFields(prev => {
            const updated = JSON.parse(JSON.stringify(prev));
            if (updated[tableIndex]?.columns?.[colIndex]) {
                updated[tableIndex].columns[colIndex][field] = value;
            }
            return updated;
        });
        setHasChanges(true);
    }, []);

    // 테이블 컬럼 삭제 핸들러
    const handleRemoveTableColumn = useCallback((tableIndex, colIndex) => {
        setEditableTableFields(prev => {
            const updated = JSON.parse(JSON.stringify(prev));
            if (updated[tableIndex]?.columns) {
                updated[tableIndex].columns.splice(colIndex, 1);
                // 테이블의 컬럼이 모두 삭제되면 테이블 자체도 삭제
                if (updated[tableIndex].columns.length === 0) {
                    updated.splice(tableIndex, 1);
                }
            }
            return updated;
        });
        setHasChanges(true);
    }, []);

    // 프롬프트 재생성
    const regeneratePrompt = useCallback(() => {
        if (currentTemplate) {
            const newPrompt = generatePromptFromFields(
                currentTemplate.name,
                editableFields,
                editableTableFields
            );
            setPrompt(newPrompt);
        }
    }, [currentTemplate, editableFields, editableTableFields, generatePromptFromFields]);

    // 템플릿 저장
    const handleSaveTemplate = useCallback(async () => {
        if (!currentTemplate || !apiBaseUrl) return;

        setIsSaving(true);
        try {
            // fields 배열은 singleFields만 포함 (테이블 컬럼은 tableFields에만 저장)
            // 테이블 컬럼을 fields에 넣으면 템플릿 관리에서 중복 표시됨

            // 🔧 FIX: Also update examples object for Template Management compatibility
            const updatedExamples = {};
            editableFields.forEach(f => {
                if (f.exampleValue) updatedExamples[f.key] = f.exampleValue;
            });
            editableTableFields.forEach(table => {
                table.columns?.forEach(col => {
                    if (col.exampleValue) updatedExamples[col.key] = col.exampleValue;
                });
            });

            // 🔧 FIX: Normalize table column fields (copy exampleValue to example for TemplateManagement compatibility)
            const normalizedTableFields = editableTableFields.map(table => ({
                ...table,
                columns: table.columns?.map(col => ({
                    ...col,
                    example: col.exampleValue || col.example || '', // TemplateManagement uses 'example'
                    exampleValue: col.exampleValue || col.example || '' // Keep both in sync
                })) || []
            }));

            const updatedTemplate = {
                ...currentTemplate,
                singleFields: editableFields,
                tableFields: normalizedTableFields, // 🔧 Use normalized table fields
                fields: editableFields, // 단일 필드만 (레거시 호환)
                examples: updatedExamples, // 🔧 Sync examples for Template Management
            };

            const response = await fetch(`${apiBaseUrl}/api/templates/${currentTemplate.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updatedTemplate),
            });

            if (response.ok) {
                setHasChanges(false);
                regeneratePrompt();
                if (onTemplateUpdate) {
                    // 서버 응답으로 전체 리스트 재조회 또는 템플릿 업데이트
                    onTemplateUpdate();
                }
                console.log('✅ 템플릿 저장 완료');
            } else {
                console.error('❌ 템플릿 저장 실패');
            }
        } catch (error) {
            console.error('❌ 템플릿 저장 오류:', error);
        } finally {
            setIsSaving(false);
        }
    }, [currentTemplate, editableFields, editableTableFields, apiBaseUrl, regeneratePrompt, onTemplateUpdate]);

    // 추출 실행
    const handleRun = useCallback(() => {
        if (onRun) {
            onRun(prompt, selectedModel);
            setActiveTab('results'); // 실행 후 결과 탭으로 전환
        }
    }, [onRun, prompt, selectedModel]);

    if (!selectedFile) {
        return (
            <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                <p>왼쪽에서 파일을 선택하세요</p>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full bg-white">
            {/* ── Row 1: 툴바 (AI추출, 검증, 템플릿, 도구) ── */}
            {onExtract && (
                <div className="flex items-center gap-1 px-2 py-1 border-b border-gray-300 bg-white flex-shrink-0" style={{ minHeight: '34px' }}>
                    {/* 딥러닝 버튼 */}
                    {isExtracting ? (
                        <button
                            onClick={onCancelExtract}
                            className="flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded transition-all"
                            style={{
                                backgroundColor: '#5F6368',
                                color: '#FFFFFF',
                                border: '1px solid #3C4043',
                            }}
                            title="처리를 취소합니다"
                        >
                            <X size={13} />
                            취소
                        </button>
                    ) : (
                        <button
                            onClick={onExtract}
                            disabled={extractDisabled}
                            className="flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded transition-all"
                            style={{
                                backgroundColor: extractDisabled ? '#E5E7EB' : '#1F2937',
                                color: extractDisabled ? '#9CA3AF' : '#FFFFFF',
                                cursor: extractDisabled ? 'not-allowed' : 'pointer',
                                border: '1px solid ' + (extractDisabled ? '#D1D5DB' : '#111827'),
                            }}
                            title="선택한 파일의 딥러닝 추출을 시작합니다"
                        >
                            <Sparkles size={13} />
                            {extractLabel}
                        </button>
                    )}

                    {/* 검증 체크박스 */}
                    <label className="flex items-center gap-1 cursor-pointer ml-1" title="추출 시 검증도 함께 실행 (추후 구현)">
                        <input
                            type="checkbox"
                            checked={verifyEnabled}
                            onChange={(e) => setVerifyEnabled(e.target.checked)}
                            className="w-3 h-3 accent-blue-600 rounded"
                        />
                        <span className="text-xs text-gray-500">검증</span>
                    </label>

                    {/* 구분선 */}
                    <div className="w-px h-4 bg-gray-300 mx-0.5" />

                    {/* 템플릿 선택 콤보 */}
                    <select
                        value={selectedTemplateId}
                        onChange={(e) => {
                            if (onTemplateChange) onTemplateChange(e.target.value);
                        }}
                        className="text-xs border border-gray-300 rounded px-1.5 py-1 bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500 max-w-[140px] truncate"
                        title="템플릿 선택"
                    >
                        {Object.values(templates).map(t => (
                            <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                    </select>
                    {/* 템플릿 관리 (설정 아이콘) */}
                    {onOpenTemplateManager && (
                        <button
                            onClick={() => onOpenTemplateManager('edit')}
                            className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                            title="템플릿 관리"
                        >
                            <Settings size={14} />
                        </button>
                    )}

                    {/* 구분선 */}
                    <div className="w-px h-4 bg-gray-300 mx-0.5" />

                    {/* 도구 드롭다운 */}
                    <div className="relative" ref={toolsMenuRef}>
                        <button
                            className={`flex items-center gap-1 px-2 py-1 text-xs rounded transition-colors ${showToolsMenu ? 'text-blue-600 bg-blue-50' : 'text-gray-600 hover:bg-gray-100'
                                }`}
                            title="도구"
                            onClick={() => setShowToolsMenu(!showToolsMenu)}
                        >
                            <Wrench size={13} />
                            도구
                        </button>
                        {showToolsMenu && (
                            <div
                                className="absolute top-full left-0 mt-1 w-44 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1"
                                onClick={() => setShowToolsMenu(false)}
                            >
                                <div className="px-3 py-1.5 text-xs font-semibold text-gray-900 border-b border-gray-100 mb-1">도구</div>
                                <button className="w-full flex items-center gap-2.5 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 transition-colors"
                                    onClick={() => setShowFindReplace(true)}
                                >
                                    <Search size={14} className="text-[#3C4043]" />
                                    찾기/바꾸기
                                </button>
                                <button className="w-full flex items-center gap-2.5 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 transition-colors">
                                    <Clipboard size={14} className="text-[#3C4043]" />
                                    클립보드
                                </button>
                                <button className="w-full flex items-center gap-2.5 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 transition-colors">
                                    <ArrowLeftRight size={14} className="text-[#3C4043]" />
                                    ,. 스위칭
                                </button>
                                <button className="w-full flex items-center gap-2.5 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 transition-colors">
                                    <Ruler size={14} className="text-[#3C4043]" />
                                    줄 자
                                </button>
                                <div className="border-t border-gray-100 my-1" />
                                <button className="w-full flex items-center gap-2.5 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 transition-colors">
                                    <Trash size={14} className="text-[#3C4043]" />
                                    캐시 비우기
                                </button>
                            </div>
                        )}
                    </div>

                    {/* 오른쪽 여백 + 초기화 */}
                    <div className="flex-1" />
                    {onReset && (
                        <button
                            onClick={onReset}
                            className="flex items-center gap-1 px-2 py-1 text-xs text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                            title="추출 결과 초기화"
                        >
                            <RefreshCw size={12} />
                            초기화
                        </button>
                    )}
                </div>
            )}

            {/* 탭 콘텐츠 + 페이지 리스트 (가로 배치) */}
            <div className="flex-1 overflow-hidden flex flex-row">
                {/* 페이지 리스트 (DocuAI 스타일) - 왼쪽에 배치 */}
                {console.log('🔍 [PageList] totalPages:', totalPages, 'shouldRender:', totalPages >= 1)}
                {totalPages >= 1 && (
                    <div className="w-10 flex-shrink-0 border-r border-gray-200 bg-gray-50 overflow-y-auto">
                        {/* 헤더 - 전체 선택 체크박스 포함 */}
                        <div className="sticky top-0 bg-gray-200 flex items-center gap-0.5 pl-1 pr-0.5 py-1 border-b border-gray-300">
                            <input
                                type="checkbox"
                                checked={checkedPages.length === totalPages}
                                ref={(el) => {
                                    if (el) {
                                        el.indeterminate = checkedPages.length > 0 && checkedPages.length < totalPages;
                                    }
                                }}
                                onChange={() => {
                                    if (checkedPages.length === totalPages) {
                                        // 전체 해제
                                        if (onPageToggle) {
                                            // 모든 페이지 해제 - setCheckedPages([])와 동일한 효과
                                            checkedPages.forEach(p => onPageToggle(p));
                                        }
                                    } else {
                                        // 전체 선택
                                        const allPages = Array.from({ length: totalPages }, (_, i) => i + 1);
                                        allPages.forEach(p => {
                                            if (!checkedPages.includes(p) && onPageToggle) {
                                                onPageToggle(p);
                                            }
                                        });
                                    }
                                }}
                                className="w-3.5 h-3.5 accent-blue-600 cursor-pointer"
                                title={checkedPages.length === totalPages ? '전체 해제' : '전체 선택'}
                            />
                            <span className="text-xs font-bold text-gray-700">P</span>
                        </div>
                        {/* 페이지 버튼 리스트 */}
                        <div className="flex flex-col">
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => {
                                const isChecked = checkedPages.includes(pageNum);
                                const isSelected = pageNum === selectedPageNumber;
                                return (
                                    <div
                                        key={pageNum}
                                        className={`flex items-center gap-0.5 pl-0.5 pr-0.5 py-1 border-b border-gray-100 transition-colors cursor-pointer ${isSelected
                                            ? 'bg-blue-100 border-r-2 border-r-blue-500'
                                            : isChecked
                                                ? 'bg-white hover:bg-blue-50'
                                                : 'bg-gray-50 hover:bg-gray-100'
                                            }`}
                                        onClick={() => {
                                            // 행 클릭 시 페이지 이동만 (체크박스는 별도 처리)
                                            console.log('🖱️ [P리스트] 행 클릭:', pageNum);
                                            if (onPageChange) {
                                                onPageChange(pageNum);
                                            }
                                        }}
                                    >
                                        {/* 체크박스 - label로 감싸서 클릭 영역 확대 */}
                                        <label
                                            className="flex items-center justify-center w-5 h-5 cursor-pointer"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={isChecked}
                                                onChange={(e) => {
                                                    e.stopPropagation();
                                                    console.log('☑️ [체크박스] 토글:', pageNum, '→', !isChecked);

                                                    // 🔧 Shift+클릭 범위 선택 (Excel 스타일)
                                                    if (e.shiftKey && lastClickedPage !== null && onPageToggle) {
                                                        const start = Math.min(lastClickedPage, pageNum);
                                                        const end = Math.max(lastClickedPage, pageNum);

                                                        // 범위 내 모든 페이지 토글 (선택 또는 해제)
                                                        for (let p = start; p <= end; p++) {
                                                            if (!checkedPages.includes(p)) {
                                                                onPageToggle(p);
                                                            }
                                                        }
                                                        console.log('🖱️ [Shift+클릭] 범위 선택:', { start, end, checkedPages });
                                                    } else {
                                                        // 일반 클릭 - 단일 토글
                                                        if (onPageToggle) {
                                                            onPageToggle(pageNum);
                                                        }
                                                    }

                                                    // 마지막 클릭 페이지 기록
                                                    setLastClickedPage(pageNum);
                                                }}
                                                className="w-3.5 h-3.5 accent-blue-600 cursor-pointer"
                                                title={isChecked ? '선택 해제 (Shift+클릭: 범위 선택)' : '선택 (Shift+클릭: 범위 선택)'}
                                            />
                                        </label>
                                        {/* 페이지 번호 */}
                                        <span className={`text-xs font-medium ${isSelected ? 'text-blue-700' : isChecked ? 'text-gray-700' : 'text-gray-400'
                                            }`}>
                                            {pageNum}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* 탭 콘텐츠 */}
                <div className="flex-1 overflow-hidden">
                    {/* 탭 1: 필드 설정 */}
                    {activeTab === 'fields' && (
                        <div className="h-full flex flex-col">
                            {/* 헤더 */}
                            <div className="px-3 py-2 bg-gradient-to-r from-blue-50 to-white flex items-center justify-between border-b border-gray-100 flex-shrink-0">
                                <div className="flex items-center gap-2">
                                    <span className="text-xs font-medium text-gray-700">
                                        {currentTemplate?.name || '템플릿 선택됨'}
                                    </span>
                                    <span className="text-xs text-gray-500">
                                        ({editableFields.length}개 단일 + {editableTableFields.reduce((acc, t) => acc + (t.columns?.length || 0), 0)}개 테이블 컬럼)
                                    </span>
                                </div>
                                <div className="flex items-center gap-2">
                                    {onOpenTemplateManager && (
                                        <>
                                            <button
                                                onClick={() => onOpenTemplateManager('create')}
                                                className="flex items-center gap-1 px-2 py-1 text-xs text-green-600 hover:bg-green-50 rounded transition-colors mr-1 border border-green-100"
                                                title="새 템플릿 만들기"
                                            >
                                                <Plus size={12} />
                                                신규
                                            </button>
                                            <button
                                                onClick={() => onOpenTemplateManager('list')}
                                                className="flex items-center gap-1 px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 rounded transition-colors mr-2 border border-blue-100"
                                                title="전체 템플릿 관리 열기"
                                            >
                                                <LayoutTemplate size={12} />
                                                템플릿 관리
                                            </button>
                                        </>
                                    )}
                                    <button
                                        onClick={regeneratePrompt}
                                        className="flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded transition-colors"
                                        title="프롬프트 재생성"
                                    >
                                        <RefreshCw size={12} />
                                        갱신
                                    </button>
                                    <button
                                        onClick={handleSaveTemplate}
                                        disabled={!hasChanges || isSaving}
                                        className={`flex items-center gap-1 px-2 py-1 text-xs rounded transition-colors ${hasChanges
                                            ? 'bg-blue-500 text-white hover:bg-blue-600'
                                            : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                            }`}
                                    >
                                        {isSaving ? (
                                            <><RefreshCw size={12} className="animate-spin" /> 저장 중...</>
                                        ) : (
                                            <><Save size={12} /> 저장</>
                                        )}
                                    </button>
                                </div>
                            </div>

                            {/* 간단한 가이드 */}
                            <div className="px-3 py-1.5 border-b border-gray-100 text-xs text-gray-500 flex items-center gap-1">
                                <span>💡</span>
                                <span>description/example 편집 가능.</span>
                                {onOpenTemplateManager && (
                                    <button onClick={onOpenTemplateManager} className="underline hover:text-gray-700">
                                        템플릿 관리
                                    </button>
                                )}
                            </div>

                            {/* 필드 테이블 */}
                            <div className="flex-1 overflow-auto">
                                <table className="w-full text-xs">
                                    <thead className="bg-gray-50 sticky top-0">
                                        <tr>
                                            <th className="px-2 py-1.5 text-left font-medium text-gray-600 w-[18%]">필드명</th>
                                            <th className="px-2 py-1.5 text-left font-medium text-gray-600 w-[8%]">타입</th>
                                            <th className="px-2 py-1.5 text-left font-medium text-gray-600 w-[32%]">DESCRIPTION</th>
                                            <th className="px-2 py-1.5 text-left font-medium text-gray-600 w-[32%]">EXAMPLE</th>
                                            <th className="px-2 py-1.5 text-center font-medium text-gray-600 w-[10%]">삭제</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {/* 단일 필드 */}
                                        {editableFields.map((field, index) => (
                                            <tr key={`single-${index}`} className="border-t border-gray-100 hover:bg-blue-50/30">
                                                <td className="px-2 py-1.5">
                                                    <span className="font-medium text-blue-700">{field.key}</span>
                                                    {field.displayName && (
                                                        <span className="ml-1 text-gray-400 text-[10px]">({field.displayName})</span>
                                                    )}
                                                </td>
                                                <td className="px-2 py-1.5">
                                                    <span className="px-1.5 py-0.5 bg-gray-100 rounded text-gray-600">
                                                        {field.type || 'string'}
                                                    </span>
                                                </td>
                                                <td className="px-2 py-1.5">
                                                    <div className="flex items-center gap-1">
                                                        <input
                                                            type="text"
                                                            value={field.description || ''}
                                                            onChange={(e) => handleFieldChange(index, 'description', e.target.value)}
                                                            className="flex-1 px-2 py-1 border border-gray-200 rounded text-xs focus:border-blue-400 focus:ring-1 focus:ring-blue-100 outline-none"
                                                            placeholder="필드 설명..."
                                                        />
                                                        <button
                                                            onClick={() => openExpandedEdit('single', index, field.key, 'description', field.description || '')}
                                                            className="p-1 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded transition-colors flex-shrink-0"
                                                            title="확장 편집"
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" /></svg>
                                                        </button>
                                                    </div>
                                                </td>
                                                <td className="px-2 py-1.5">
                                                    <div className="flex items-center gap-1">
                                                        <input
                                                            type="text"
                                                            value={field.exampleValue || ''}
                                                            onChange={(e) => handleFieldChange(index, 'exampleValue', e.target.value)}
                                                            className="flex-1 px-2 py-1 border border-gray-200 rounded text-xs focus:border-blue-400 focus:ring-1 focus:ring-blue-100 outline-none font-mono"
                                                            placeholder="예시 값..."
                                                        />
                                                        <button
                                                            onClick={() => openExpandedEdit('single', index, field.key, 'exampleValue', field.exampleValue || '')}
                                                            className="p-1 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded transition-colors flex-shrink-0"
                                                            title="확장 편집"
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" /></svg>
                                                        </button>
                                                    </div>
                                                </td>
                                                <td className="px-2 py-1.5 text-center">
                                                    <button
                                                        onClick={() => handleRemoveField(index)}
                                                        className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                                                        title="필드 삭제 (저장 시 적용)"
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}

                                        {/* 테이블 필드 구분선 */}
                                        {editableTableFields.length > 0 && (
                                            <tr className="bg-gray-50">
                                                <td colSpan={5} className="px-2 py-1.5 font-medium text-gray-700 text-[10px]">
                                                    📊 테이블 필드 (Table Columns)
                                                </td>
                                            </tr>
                                        )}

                                        {/* 테이블 컬럼 */}
                                        {editableTableFields.map((table, tableIndex) => (
                                            table.columns?.map((col, colIndex) => (
                                                <tr key={`table-${tableIndex}-${colIndex}`} className="border-t border-gray-100 hover:bg-gray-50/30">
                                                    <td className="px-2 py-1.5">
                                                        <span className="font-medium text-gray-700">{col.key}</span>
                                                        <span className="ml-1 text-gray-400 text-[10px]">
                                                            ({table.tableName || 'items'})
                                                        </span>
                                                    </td>
                                                    <td className="px-2 py-1.5">
                                                        <span className="px-1.5 py-0.5 bg-gray-100 rounded text-gray-700">
                                                            {col.type || 'string'}
                                                        </span>
                                                    </td>
                                                    <td className="px-2 py-1.5">
                                                        <div className="flex items-center gap-1">
                                                            <input
                                                                type="text"
                                                                value={col.description || ''}
                                                                onChange={(e) => handleTableColumnChange(tableIndex, colIndex, 'description', e.target.value)}
                                                                className="flex-1 px-2 py-1 border border-gray-200 rounded text-xs focus:border-gray-400 focus:ring-1 focus:ring-amber-100 outline-none"
                                                                placeholder="컬럼 설명..."
                                                            />
                                                            <button
                                                                onClick={() => openExpandedEdit('table', -1, `${table.tableName}.${col.key}`, 'description', col.description || '', tableIndex, colIndex)}
                                                                className="p-1 text-gray-400 hover:text-gray-500 hover:bg-gray-50 rounded transition-colors flex-shrink-0"
                                                                title="확장 편집"
                                                            >
                                                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" /></svg>
                                                            </button>
                                                        </div>
                                                    </td>
                                                    <td className="px-2 py-1.5">
                                                        <div className="flex items-center gap-1">
                                                            <input
                                                                type="text"
                                                                value={col.exampleValue || ''}
                                                                onChange={(e) => handleTableColumnChange(tableIndex, colIndex, 'exampleValue', e.target.value)}
                                                                className="flex-1 px-2 py-1 border border-gray-200 rounded text-xs focus:border-gray-400 focus:ring-1 focus:ring-amber-100 outline-none font-mono"
                                                                placeholder="예시 값..."
                                                            />
                                                            <button
                                                                onClick={() => openExpandedEdit('table', -1, `${table.tableName}.${col.key}`, 'exampleValue', col.exampleValue || '', tableIndex, colIndex)}
                                                                className="p-1 text-gray-400 hover:text-gray-500 hover:bg-gray-50 rounded transition-colors flex-shrink-0"
                                                                title="확장 편집"
                                                            >
                                                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" /></svg>
                                                            </button>
                                                        </div>
                                                    </td>
                                                    <td className="px-2 py-1.5 text-center">
                                                        <button
                                                            onClick={() => handleRemoveTableColumn(tableIndex, colIndex)}
                                                            className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                                                            title="컬럼 삭제 (저장 시 적용)"
                                                        >
                                                            <Trash2 size={14} />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        ))}

                                        {/* 빈 상태 */}
                                        {editableFields.length === 0 && editableTableFields.length === 0 && (
                                            <tr>
                                                <td colSpan={5} className="px-4 py-8 text-center text-gray-400">
                                                    선택된 템플릿에 필드가 없습니다.
                                                    <br />
                                                    <span className="text-xs">템플릿 관리에서 필드를 추가해주세요.</span>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* 필드 추가 버튼 */}
                            <div className="px-3 py-2 border-t border-gray-100 flex items-center gap-2 flex-shrink-0">
                                <button
                                    onClick={() => setShowAddFieldModal(true)}
                                    className="flex items-center gap-1 px-3 py-1.5 text-xs bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
                                >
                                    <Plus size={14} />
                                    필드 추가
                                </button>
                                <span className="text-xs text-gray-400">
                                    저장 버튼을 눌러야 템플릿에 반영됩니다
                                </span>
                            </div>

                            {/* 필드 추가 모달 */}
                            {showAddFieldModal && (
                                <div className="absolute inset-0 bg-black/30 flex items-center justify-center z-50">
                                    <div className="bg-white p-4 rounded-lg shadow-xl w-80 border border-gray-200">
                                        <div className="flex items-center justify-between mb-4">
                                            <h3 className="font-medium text-gray-900">새 필드 추가</h3>
                                            <button
                                                onClick={() => setShowAddFieldModal(false)}
                                                className="p-1 hover:bg-gray-100 rounded"
                                            >
                                                <X size={16} className="text-gray-500" />
                                            </button>
                                        </div>
                                        <div className="space-y-3">
                                            <div>
                                                <label className="block text-xs font-medium text-gray-600 mb-1">필드명 *</label>
                                                <input
                                                    type="text"
                                                    value={newField.key}
                                                    onChange={(e) => setNewField({ ...newField, key: e.target.value })}
                                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:border-blue-400 focus:ring-1 focus:ring-blue-100 outline-none"
                                                    placeholder="예: CompanyName, TotalAmount"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-600 mb-1">타입</label>
                                                <select
                                                    value={newField.type}
                                                    onChange={(e) => setNewField({ ...newField, type: e.target.value })}
                                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:border-blue-400 outline-none"
                                                >
                                                    <option value="string">string (문자열)</option>
                                                    <option value="number">number (숫자)</option>
                                                    <option value="date">date (날짜)</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-600 mb-1">설명 (AI 참고용)</label>
                                                <input
                                                    type="text"
                                                    value={newField.description}
                                                    onChange={(e) => setNewField({ ...newField, description: e.target.value })}
                                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:border-blue-400 focus:ring-1 focus:ring-blue-100 outline-none"
                                                    placeholder="예: 회사명을 추출합니다"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-600 mb-1">예시 값</label>
                                                <input
                                                    type="text"
                                                    value={newField.exampleValue}
                                                    onChange={(e) => setNewField({ ...newField, exampleValue: e.target.value })}
                                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:border-blue-400 focus:ring-1 focus:ring-blue-100 outline-none font-mono"
                                                    placeholder="예: ABC Corporation"
                                                />
                                            </div>
                                        </div>
                                        <div className="flex gap-2 mt-4">
                                            <button
                                                onClick={handleAddField}
                                                disabled={!newField.key.trim()}
                                                className={`flex-1 px-3 py-2 text-sm rounded font-medium transition-colors ${newField.key.trim()
                                                    ? 'bg-blue-500 text-white hover:bg-blue-600'
                                                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                                    }`}
                                            >
                                                추가
                                            </button>
                                            <button
                                                onClick={() => setShowAddFieldModal(false)}
                                                className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                                            >
                                                취소
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* 🔧 프롬프트 탭 콘텐츠 삭제 (추후 필요 시 복원) */}

                    {/* 탭: 추출 결과 (PBGPanel 항상 렌더 — 탭 버튼은 PBGPanel 컨트롤 바에 통합) */}
                    <div className={`h-full overflow-auto ${activeTab !== 'results' ? 'hidden' : ''}`}>
                        {React.Children.map(children, child =>
                            child ? React.cloneElement(child, {
                                activeTab,
                                onTabChange: setActiveTab,
                                hasTabChanges: hasChanges
                            }) : null
                        )}
                    </div>

                    {/* 🔧 학습 탭 콘텐츠 삭제 (추후 필요 시 복원) */}
                </div>

            </div>

            {/* 🔧 확장 편집 모달 */}
            {expandedEditModal.isOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setExpandedEditModal(prev => ({ ...prev, isOpen: false }))}>
                    <div
                        className="bg-white rounded-lg shadow-2xl w-[600px] max-h-[80vh] overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* 모달 헤더 */}
                        <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b">
                            <div>
                                <h3 className="font-semibold text-gray-800">필드 확장 편집</h3>
                                <p className="text-xs text-gray-500 mt-0.5">
                                    <span className="font-mono text-blue-600">{expandedEditModal.fieldName}</span>
                                    <span className="mx-1">→</span>
                                    <span className={expandedEditModal.fieldKey === 'description' ? 'text-green-600' : 'text-gray-600'}>
                                        {expandedEditModal.fieldKey === 'description' ? 'DESCRIPTION' : 'EXAMPLE'}
                                    </span>
                                </p>
                            </div>
                            <button
                                onClick={() => setExpandedEditModal(prev => ({ ...prev, isOpen: false }))}
                                className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        {/* 모달 바디 */}
                        <div className="p-4">
                            <textarea
                                value={expandedEditModal.value}
                                onChange={(e) => setExpandedEditModal(prev => ({ ...prev, value: e.target.value }))}
                                className="w-full h-48 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none resize-none"
                                placeholder={expandedEditModal.fieldKey === 'description' ? '필드에 대한 상세 설명을 입력하세요...' : '예시 값을 입력하세요...'}
                                autoFocus
                            />
                            <p className="mt-2 text-xs text-gray-400">
                                💡 긴 텍스트도 편리하게 입력할 수 있습니다. (현재 {expandedEditModal.value.length}자)
                            </p>
                        </div>

                        {/* 모달 푸터 */}
                        <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-t">
                            {/* AI 가이드 버튼 (왼쪽) */}
                            <button
                                onClick={handleEnhanceModalPrompt}
                                disabled={isEnhancingModalPrompt || !expandedEditModal.value.trim()}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${isEnhancingModalPrompt
                                    ? 'bg-purple-100 text-purple-400 cursor-wait'
                                    : expandedEditModal.value.trim()
                                        ? 'bg-gradient-to-r from-purple-500 to-indigo-500 text-white hover:from-purple-600 hover:to-indigo-600 shadow-sm'
                                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    }`}
                                title="AI가 입력 내용을 구조화된 프롬프트로 변환합니다"
                            >
                                {isEnhancingModalPrompt ? (
                                    <>
                                        <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                                        AI 분석 중...
                                    </>
                                ) : (
                                    <>
                                        <Sparkles className="w-3.5 h-3.5" />
                                        ✨ AI 가이드
                                    </>
                                )}
                            </button>

                            {/* 취소/적용 버튼 (오른쪽) */}
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setExpandedEditModal(prev => ({ ...prev, isOpen: false }))}
                                    className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-200 rounded-md transition-colors"
                                >
                                    취소
                                </button>
                                <button
                                    onClick={saveExpandedEdit}
                                    className="px-4 py-2 text-sm text-white bg-blue-500 hover:bg-blue-600 rounded-md transition-colors"
                                >
                                    적용
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {/* 찾기/바꾸기 모달 */}
            <FindReplaceModal
                isOpen={showFindReplace}
                onClose={() => setShowFindReplace(false)}
            />
        </div>
    );
};

export default RightPanelTabs;
